// src/hooks/useSQLiteSync.ts
import { useState, useEffect, useCallback } from 'react'
import { initializeDatabase } from '@/lib/indexeddb/database'
import { syncDataFromBackend, getSyncStatus } from '@/lib/indexeddb/syncManager'
import { useAuthStore } from '@/app/store/authStore'
import { toast } from 'sonner'
import { initializeWindowApi } from '@/lib/indexeddb/initializeWindowApi'

interface UseSQLiteSyncReturn {
  isInitialized: boolean
  isSyncing: boolean
  lastSyncTime: Date | null
  syncError: string | null
  initializeDB: () => Promise<void>
  syncData: () => Promise<void>
  needsDataSync: boolean
}

export function useSQLiteSync(): UseSQLiteSyncReturn {
  const [isInitialized, setIsInitialized] = useState(false)
  console.log('[useSQLiteSync] Hook state:', { isInitialized })
  const [isSyncing, setIsSyncing] = useState(false)
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null)
  const [syncError, setSyncError] = useState<string | null>(null)
  const [needsDataSync, setNeedsDataSync] = useState(true)
  
  const isAuthenticated = useAuthStore(state => state.isAuthenticated)
  const organizationId = useAuthStore(state => state.userOrgId)
  
  // Database is considered initialized if we've successfully called initializeDatabase
  // We track this with the isInitialized state variable
  
  // Initialize database
  const initializeDB = useCallback(async () => {
    if (isInitialized) return
    
    try {
      console.log('[useSQLiteSync] Initializing IndexedDB database...')
      await initializeDatabase()
      console.log('[useSQLiteSync] IndexedDB database initialized successfully')
      setIsInitialized(true)
      
      // Get last sync time from localStorage
      const savedSyncTime = localStorage.getItem('indexeddb_last_sync_time')
      if (savedSyncTime) {
        setLastSyncTime(new Date(savedSyncTime))
        // Check if last sync was more than 1 hour ago
        const hoursSinceSync = (Date.now() - new Date(savedSyncTime).getTime()) / (1000 * 60 * 60)
        setNeedsDataSync(hoursSinceSync > 1)
      } else {
        setNeedsDataSync(true)
      }
      
      // Initialize window.api with IndexedDB implementation
      initializeWindowApi()
    } catch (error) {
      console.error('[useSQLiteSync] Failed to initialize IndexedDB database:', error)
      setSyncError('Failed to initialize local database')
      toast.error('Failed to initialize local database')
    }
  }, [])
  
  // Sync data from backend
  const syncData = useCallback(async () => {
    if (!isInitialized || isSyncing) {
      console.warn('[useSQLiteSync] Cannot sync: not initialized or already syncing')
      return
    }
    
    if (!isAuthenticated || !organizationId) {
      console.warn('[useSQLiteSync] Cannot sync: not authenticated or no organization selected')
      return
    }
    
    try {
      setIsSyncing(true)
      setSyncError(null)
      
      console.log('[useSQLiteSync] Starting data sync...')
      toast.loading('Syncing data from server...')
      
      const result = await syncDataFromBackend()
      
      if (result.success) {
        const now = new Date()
        setLastSyncTime(now)
        setNeedsDataSync(false)
        
        // Save sync time to localStorage
        localStorage.setItem('indexeddb_last_sync_time', now.toISOString())
        
        toast.success(`Synced ${result.itemCount} items successfully`)
        console.log('[useSQLiteSync] Sync completed successfully')
      } else {
        setSyncError(result.error || 'Sync failed')
        toast.error(`Sync failed: ${result.error}`)
        console.error('[useSQLiteSync] Sync failed:', result.error)
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      setSyncError(errorMessage)
      toast.error(`Sync failed: ${errorMessage}`)
      console.error('[useSQLiteSync] Sync error:', error)
    } finally {
      setIsSyncing(false)
      toast.dismiss()
    }
  }, [isInitialized, isSyncing, isAuthenticated, organizationId])
  
  // Initialize database on mount
  useEffect(() => {
    initializeDB()
  }, [])
  
  // Removed auto-sync - sync is now manual only
  // To sync data, call the syncData() function explicitly
  
  // Update sync status periodically
  useEffect(() => {
    if (!isInitialized) return

    const interval = setInterval(async () => {
      const status = await getSyncStatus()
      if (status.lastSync) {
        setLastSyncTime(status.lastSync)
      }
    }, 10000) // Check every 10 seconds

    return () => clearInterval(interval)
  }, [isInitialized])
  
  return {
    isInitialized,
    isSyncing,
    lastSyncTime,
    syncError,
    initializeDB,
    syncData,
    needsDataSync
  }
}